import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { LayoutItem, LayoutProps, sidebarToContent, VariantConfig } from './sidebarToContent';

export type LayoutState = {
  activeLayout: LayoutItem;
  activeLayoutProps: LayoutProps;
  showWelcomeAnimation: boolean;
};

const loadLayoutStateFromLocalStorage = (): LayoutState => {
  try {
    const savedLayout = localStorage.getItem('layoutState');
    const pathname = window.location.pathname;
    const isAboutPage = pathname === '/about';

    if (isAboutPage) {
      return {
        activeLayout: 'about',
        activeLayoutProps: sidebarToContent['about'],
        showWelcomeAnimation: false,
      };
    }

    if (savedLayout) {
      const parsedLayout = JSON.parse(savedLayout);
      return {
        activeLayout: parsedLayout.activeLayout,
        activeLayoutProps: sidebarToContent[parsedLayout.activeLayout as keyof VariantConfig],
        showWelcomeAnimation: false,
      };
    }

    return {
      activeLayout: 'chat',
      activeLayoutProps: sidebarToContent['chat'],
      showWelcomeAnimation: false,
    };
  } catch (e) {
    console.error('Failed to load layout state from localStorage:', e);
  }

  return {
    activeLayout: 'chat',
    activeLayoutProps: sidebarToContent['chat'],
    showWelcomeAnimation: false,
  };
};

const initialState: LayoutState = loadLayoutStateFromLocalStorage();

const layoutSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    setCurrentLayout: (state, action: PayloadAction<LayoutItem>) => {
      state.activeLayout = action.payload;
      state.activeLayoutProps = sidebarToContent[action.payload];
      localStorage.setItem('layoutState', JSON.stringify({ activeLayout: state.activeLayout }));
    },
    disableWelcomeAnimation: (state) => {
      state.showWelcomeAnimation = false;
    },
    resetToOnboarding: (state) => {
      state.activeLayout = 'onboardingMode';
      state.activeLayoutProps = sidebarToContent['onboardingMode'];
    },
  },
});

export const { setCurrentLayout, disableWelcomeAnimation, resetToOnboarding } = layoutSlice.actions;

export default layoutSlice.reducer;

export const selectCurrentLayout = (state: { layout: LayoutState }) => state.layout.activeLayout;

export const selectCurrentLayoutProps = (state: { layout: LayoutState }) => state.layout.activeLayoutProps;

export const selectShowWelcomeAnimation = (state: { layout: LayoutState }) => state.layout.showWelcomeAnimation;
